import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { apiService } from '../services/api';
import TimingMeter from '../components/TimingMeter';
import VolMeter from '../components/VolMeter';

// ── helpers ──────────────────────────────────────────────────────────────────

const severityColor: Record<string, 'error' | 'warning' | 'info' | 'success'> = {
  error: 'error',
  warning: 'warning',
  info: 'info',
  success: 'success',
};

const riskColor = (sev: string): string =>
  ({ error: '#f44336', warning: '#ff9800', info: '#2196f3', success: '#4caf50' })[sev] ?? '#9e9e9e';

// ── Valuation KPI card ────────────────────────────────────────────────────────

function KpiCard({ title, value, unit = '', label, severity, description, mean, children }: any) {
  const color = riskColor(severity ?? 'info');
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="caption" color="text.secondary">{title}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.5 }}>
          <Typography variant="h4" fontWeight={700} sx={{ color }}>
            {value != null ? `${value}${unit}` : '—'}
          </Typography>
        </Box>
        {label && (
          <Chip
            label={label}
            size="small"
            sx={{ mt: 0.5, bgcolor: color + '22', color, fontWeight: 600, border: `1px solid ${color}` }}
          />
        )}
        {mean != null && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Historical mean: {mean}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{description}</Typography>
        {children}
      </CardContent>
    </Card>
  );
}

// ── Collapse Radar ────────────────────────────────────────────────────────────

function RiskGauge({ score, severity }: { score: number; severity: string }) {
  const color = riskColor(severity);
  return (
    <Box sx={{ textAlign: 'center', py: 2 }}>
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={140}
          thickness={6}
          sx={{ color: 'grey.200', position: 'absolute' }}
        />
        <CircularProgress
          variant="determinate"
          value={score}
          size={140}
          thickness={6}
          sx={{ color }}
        />
        <Box sx={{
          top: 0, left: 0, bottom: 0, right: 0,
          position: 'absolute', display: 'flex',
          alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
        }}>
          <Typography variant="h4" fontWeight={800} sx={{ color }}>
            {score.toFixed(0)}%
          </Typography>
          <Typography variant="caption" color="text.secondary">similarity</Typography>
        </Box>
      </Box>
    </Box>
  );
}

function CollapseRadar({ radar }: { radar: any }) {
  if (!radar) return null;
  const sev: 'error' | 'warning' | 'info' | 'success' = severityColor[radar.risk_severity] ?? 'info';
  const color = riskColor(radar.risk_severity);

  return (
    <Box>
      {/* Risk banner */}
      <Alert
        severity={sev}
        icon={sev === 'success' ? <CheckCircleOutlineIcon /> : <WarningAmberIcon />}
        sx={{ mb: 3, fontWeight: 600, fontSize: '0.95rem' }}
      >
        <strong>Collapse Risk: {radar.risk_level}</strong> — {radar.interpretation}
      </Alert>

      <Grid container spacing={3}>
        {/* Gauge + lead time */}
        <Grid item xs={12} md={3}>
          <Paper sx={{ p: 2, textAlign: 'center', height: '100%' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Pattern Similarity
            </Typography>
            <RiskGauge score={radar.risk_score_pct} severity={radar.risk_severity} />
            <Divider sx={{ my: 2 }} />
            {radar.estimated_lead_days != null && (
              <>
                <Typography variant="h5" fontWeight={700} sx={{ color }}>
                  {radar.estimated_lead_days}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  days est. lead time
                </Typography>
                {radar.estimated_lead_days_range && (
                  <Typography variant="caption" color="text.secondary">
                    (range: {radar.estimated_lead_days_range[0]}–{radar.estimated_lead_days_range[1]}d)
                  </Typography>
                )}
              </>
            )}
          </Paper>
        </Grid>

        {/* Top matches */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Most Similar Historical Events
            </Typography>
            {(radar.top_matches ?? []).map((m: any, i: number) => (
              <Box key={m.id} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight={600}>{m.name}</Typography>
                  <Chip
                    label={`${m.similarity_pct}%`}
                    size="small"
                    sx={{ fontWeight: 700, bgcolor: riskColor(i === 0 ? radar.risk_severity : 'info') + '22' }}
                  />
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={m.similarity_pct}
                  sx={{
                    my: 0.5, height: 6, borderRadius: 1,
                    '& .MuiLinearProgress-bar': {
                      bgcolor: riskColor(i === 0 ? radar.risk_severity : 'info'),
                    },
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {m.crash_date} · −{m.sp500_drawdown_pct}% S&amp;P · ~{m.typical_lead_days}d lead
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.3 }}>
                  {m.note}
                </Typography>
              </Box>
            ))}
          </Paper>
        </Grid>

        {/* KPI comparison table */}
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Current vs Pre-Crash KPIs
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '4px 6px', color: '#888', fontWeight: 500 }}>KPI</th>
                    <th style={{ textAlign: 'right', padding: '4px 6px', color: '#888', fontWeight: 500 }}>Now</th>
                    {(radar.top_matches ?? []).slice(0, 2).map((m: any) => (
                      <th key={m.id} style={{ textAlign: 'right', padding: '4px 6px', color: '#888', fontWeight: 500 }}>
                        {m.name.split(' ')[0]}
                      </th>
                    ))}
                    <Tooltip title="Percentile rank against the full historical distribution (e.g. 80% = more dangerous than 80% of all historical readings). Self-normalises across regimes — not affected by structural VIX drift or Buffett Indicator expansion.">
                      <th style={{ textAlign: 'right', padding: '4px 6px', color: '#888', fontWeight: 500, cursor: 'help', borderBottom: '1px dashed #aaa' }}>
                        Hist. %ile ⓘ
                      </th>
                    </Tooltip>
                  </tr>
                </thead>
                <tbody>
                  {(radar.kpi_table ?? []).map((row: any) => {
                    // Use true danger percentile if available, else fall back to fixed-bound normalised score
                    const dangerPct = row.danger_percentile ?? row.danger_normalised ?? null;
                    const danger = dangerPct >= 60;
                    const extreme = dangerPct >= 80;
                    const cellColor = extreme ? '#f44336' : danger ? '#ff9800' : 'inherit';
                    const unitFmt = (v: number | null) =>
                      v == null ? '—' : `${v.toFixed(row.unit === '%' ? 2 : 1)}${row.unit}`;
                    const tooltipText = [
                      row.danger_direction === 'low' ? '⚠ Lower value = more danger (complacency signal)' : '⚠ Higher value = more danger',
                      `Full-history mean (incl. crisis spikes): ${unitFmt(row.historical_mean)}`,
                      row.regime_note || '',
                    ].filter(Boolean).join('\n\n');
                    const pctDisplay = dangerPct != null
                      ? `${Math.round(dangerPct)}%`
                      : '—';
                    return (
                      <tr key={row.kpi} style={{ borderTop: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '5px 6px', fontWeight: 500 }}>
                          <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{tooltipText}</span>} placement="right">
                            <span style={{ cursor: 'help', borderBottom: '1px dashed #aaa' }}>{row.label}</span>
                          </Tooltip>
                        </td>
                        <td style={{ textAlign: 'right', padding: '5px 6px', fontWeight: 700, color: cellColor }}>
                          {unitFmt(row.current)}
                        </td>
                        {(radar.top_matches ?? []).slice(0, 2).map((m: any) => (
                          <td key={m.id} style={{ textAlign: 'right', padding: '5px 6px', color: '#666' }}>
                            {unitFmt(row.crash_values?.[m.id] ?? null)}
                          </td>
                        ))}
                        <td style={{ textAlign: 'right', padding: '5px 6px', fontWeight: 700, color: cellColor }}>
                          {pctDisplay}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Red = extreme danger (&gt;80th %ile) · Orange = elevated (&gt;60th %ile) · Hover KPI name for regime context
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Alert severity="info" sx={{ mt: 2 }} icon={false}>
        <Typography variant="caption">{radar.disclaimer}</Typography>
      </Alert>
    </Box>
  );
}

// ── Collapse Stages ──────────────────────────────────────────────────────────

const STAGE_COLORS: Record<number, string> = {
  0: '#4caf50',
  1: '#8bc34a',
  2: '#ffc107',
  3: '#ff9800',
  4: '#f44336',
  5: '#9c27b0',
};

function DangerBar({ label, value, tooltip }: { label: string; value: number | null; tooltip: string }) {
  const pct = value != null ? Math.round(value * 100) : null;
  const color = pct == null ? '#9e9e9e'
    : pct >= 80 ? '#f44336'
    : pct >= 60 ? '#ff9800'
    : pct >= 40 ? '#ffc107'
    : '#4caf50';
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
        <Tooltip title={tooltip}>
          <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>{label}</Typography>
        </Tooltip>
        <Typography variant="caption" fontWeight={700} sx={{ color }}>
          {pct != null ? `${pct}th pct` : '—'}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct ?? 0}
        sx={{ height: 8, borderRadius: 1, bgcolor: 'grey.200',
          '& .MuiLinearProgress-bar': { bgcolor: color } }}
      />
    </Box>
  );
}

function StageTimeline({ history }: { history: any[] }) {
  if (!history?.length) return null;
  // Show last 24 months
  const slice = history.slice(-24);
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        Stage history (last 24 months)
      </Typography>
      <Box sx={{ display: 'flex', gap: '2px', flexWrap: 'nowrap', overflowX: 'auto' }}>
        {slice.map((row: any) => (
          <Tooltip key={row.date} title={`${row.date}: Stage ${row.stage} — ${row.stage_name}`}>
            <Box sx={{
              width: 18, height: 28, flexShrink: 0, borderRadius: 0.5,
              bgcolor: STAGE_COLORS[row.stage as number] ?? '#9e9e9e',
              cursor: 'default',
            }} />
          </Tooltip>
        ))}
      </Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
        {Object.entries(STAGE_COLORS).map(([s, c]) => (
          <Box key={s} sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
            <Box sx={{ width: 10, height: 10, bgcolor: c, borderRadius: 0.3 }} />
            <Typography variant="caption" color="text.secondary">S{s}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const INPUT_NAMES: Record<string, string> = {
  cape: 'CAPE', buffett_us: 'Buffett indicator', vix: 'VIX', yield_curve: 'Yield curve', credit_spread: 'Credit spread',
};

function CollapseStages({ stages }: { stages: any }) {
  if (!stages) return null;
  const cs = stages.current_stage;
  const stageColor = STAGE_COLORS[cs.stage as number] ?? '#9e9e9e';
  const dp = cs.danger_percentiles ?? {};
  const kpis = cs.current_kpis ?? {};
  const defs = stages.stage_definitions ?? {};

  return (
    <Box>
      {/* Current stage banner */}
      <Paper sx={{ p: 3, mb: 3, borderLeft: `6px solid ${stageColor}` }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <TrendingUpIcon sx={{ color: stageColor }} />
              <Typography variant="h6" fontWeight={700} sx={{ color: stageColor }}>
                {cs.stage_label}
              </Typography>
              <Chip
                label={`${cs.months_in_stage}m in stage`}
                size="small"
                sx={{ bgcolor: stageColor + '22', color: stageColor, fontWeight: 600, border: `1px solid ${stageColor}` }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 640 }}>
              {cs.stage_description}
            </Typography>
            {cs.fall_odds ? (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="body2" sx={{ maxWidth: 640 }}>
                  <strong>What followed months like this:</strong> the S&amp;P 500 fell 10%+ within 9 months{' '}
                  <strong>{Math.round(cs.fall_odds.fall_rate * 100)}%</strong> of the time, vs{' '}
                  {Math.round(cs.fall_odds.base_fall_rate * 100)}% for all months since {cs.fall_odds.since.slice(0, 4)}{' '}
                  (<strong>{cs.fall_odds.verdict}</strong>). A 20%+ fall within 12 months:{' '}
                  {Math.round(cs.fall_odds.big_fall_rate * 100)}% vs {Math.round(cs.fall_odds.base_big_fall_rate * 100)}%.
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Based on {cs.fall_odds.months} months in this stage. A stage is a description of today's
                  readings, not a countdown: the market has stayed in one stage for years without a crash. As of {cs.as_of}.
                </Typography>
              </Box>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                As of <strong>{cs.as_of}</strong>
              </Typography>
            )}
            {(() => {
              const bad = Object.entries(cs.inputs ?? {}).filter(([, v]: [string, any]) => v.status !== 'live' && v.status !== 'live value today');
              return bad.length ? (
                <Alert severity="warning" sx={{ mt: 1.5, maxWidth: 640 }}>
                  {bad.length >= 3 ? 'This stage is unreliable: ' : 'Some inputs are not current: '}
                  {bad.map(([k, v]: [string, any]) => `${INPUT_NAMES[k] ?? k} (${v.status})`).join(', ')}.
                  {bad.some(([, v]: [string, any]) => v.status === 'missing') && ' The data source (FRED) may be down; the stage is computed from the remaining inputs.'}
                </Alert>
              ) : null;
            })()}
          </Box>

          {/* KPI snapshot */}
          <Box sx={{ minWidth: 180 }}>
            {[
              ['CAPE', kpis.cape?.toFixed(1)],
              ['Buffett %', kpis.buffett_us?.toFixed(0) + '%'],
              ['VIX', kpis.vix?.toFixed(1)],
              ['10Y-2Y', kpis.yield_curve?.toFixed(2) + '%'],
              ['Credit Spr', kpis.credit_spread?.toFixed(2) + '%'],
            ].map(([label, val]) => (
              <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.2 }}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="caption" fontWeight={600}>{val ?? '—'}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Danger bars */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Danger Percentiles
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              Each KPI ranked against its full history. 100th = most dangerous ever.
            </Typography>
            <DangerBar label="CAPE ratio"        value={dp.cape_d}    tooltip="Higher CAPE = more overvalued vs history" />
            <DangerBar label="Buffett Indicator" value={dp.buffett_d}  tooltip="Higher market cap/GDP = more stretched" />
            <DangerBar label="VIX complacency"  value={dp.vix_d}     tooltip="LOW VIX = complacency = pre-crash danger (reversed)" />
            <DangerBar label="Yield curve"       value={dp.yc_d}      tooltip="Flatter/inverted curve = more danger (reversed)" />
            <DangerBar label="Credit spreads"    value={dp.credit_d}  tooltip="TIGHT spreads = complacency = pre-crash danger (reversed)" />
          </Paper>
        </Grid>

        {/* Stage timeline */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Stage Progression
            </Typography>
            <StageTimeline history={stages.recent_history} />
          </Paper>
        </Grid>

        {/* Stage reference guide */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Stage Reference
            </Typography>
            {Object.entries(defs).map(([sid, def]: [string, any]) => (
              <Box key={sid} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                <Box sx={{
                  width: 10, height: 10, mt: 0.4, flexShrink: 0, borderRadius: '50%',
                  bgcolor: STAGE_COLORS[Number(sid)] ?? '#9e9e9e',
                }} />
                <Box>
                  <Typography variant="caption" fontWeight={600}>{def.label}</Typography>
                  {def.measured && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      10%+ fall within 9 months followed {Math.round(def.measured.fall_rate * 100)}% of
                      its {def.measured.months} months
                      {stages.stage_outcomes?.all_months && ` (all months ${Math.round(stages.stage_outcomes.all_months.fall_rate * 100)}%)`}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function MarketValuation() {
  const [valuation, setValuation] = useState<any>(null);
  const [radar, setRadar] = useState<any>(null);
  const [stages, setStages] = useState<any>(null);
  const [breadthData, setBreadthData] = useState<any>(null);
  const [meter, setMeter] = useState<any>(null);
  const [meterLoading, setMeterLoading] = useState(true);
  const [volMeter, setVolMeter] = useState<any>(null);
  const [volLoading, setVolLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [radarLoading, setRadarLoading] = useState(true);
  const [stagesLoading, setStagesLoading] = useState(true);
  const [breadthLoading, setBreadthLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiService.getMarketValuation()
      .then(setValuation)
      .catch(e => setError(e?.message ?? 'Failed to load'))
      .finally(() => setLoading(false));

    apiService.getVolMeter()
      .then(setVolMeter)
      .catch(() => {})
      .finally(() => setVolLoading(false));

    apiService.getTimingMeter()
      .then(setMeter)
      .catch(() => {})
      .finally(() => setMeterLoading(false));

    apiService.getCollapseRadar()
      .then(setRadar)
      .catch(() => {})
      .finally(() => setRadarLoading(false));

    apiService.getCollapseStages()
      .then(setStages)
      .catch(() => {})
      .finally(() => setStagesLoading(false));

    apiService.getMarketBreadth()
      .then(setBreadthData)
      .catch(() => {})
      .finally(() => setBreadthLoading(false));
  }, []);

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Market Valuation &amp; Collapse Radar
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Macro valuation metrics vs. historical crash patterns. Cached 1–6 hours.
      </Typography>

      {/* ── Volatility (VIX / VXX) Meter ── */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
        Volatility Meter — VIX / VXX
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        How crazy the market is now, how crazy next month has tended to get from here, and what VXX did next from the same
        situation. VXX loses value steadily while markets are calm, so it pays only in the rare large spikes.
      </Typography>
      {volLoading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : volMeter ? (
        <VolMeter meter={volMeter} />
      ) : (
        <Alert severity="warning">Volatility meter unavailable.</Alert>
      )}

      <Divider sx={{ my: 4 }} />

      {/* ── Buy / Sell Meter ── */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
        Buy / Sell Meter
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        When to be in the stock market and when to step out, from the rules that held up across 1955–2026.
        No rule predicts the date of a crash; this one aims to sidestep long, recession-driven bear markets.
      </Typography>
      {meterLoading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : meter ? (
        <TimingMeter meter={meter} />
      ) : (
        <Alert severity="warning">Buy / sell meter unavailable.</Alert>
      )}

      <Divider sx={{ my: 4 }} />

      {/* ── Collapse Radar ── */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        Collapse Radar
      </Typography>
      {radarLoading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : radar ? (
        <CollapseRadar radar={radar} />
      ) : (
        <Alert severity="warning">Collapse radar unavailable.</Alert>
      )}

      <Divider sx={{ my: 4 }} />

      {/* ── Collapse Stage Engine ── */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        Market Cycle Stage
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Full 50-year KPI time series normalised to danger percentiles, then classified into
        six named stages found before and during the ten historical market collapses.
      </Typography>
      {stagesLoading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : stages ? (
        <CollapseStages stages={stages} />
      ) : (
        <Alert severity="warning">Stage analysis unavailable.</Alert>
      )}

      {/* ── Yield Curve Uninversion Warning ── */}
      {(() => {
        const ycs = stages?.current_stage?.yield_curve_status;
        if (!ycs) return null;
        if (ycs.uninversion_detected && ycs.warning) {
          return (
            <Alert severity="error" icon={<WarningAmberIcon />} sx={{ mt: 2 }}>
              <strong>Yield Curve Uninversion Detected</strong><br />
              {ycs.warning}
            </Alert>
          );
        }
        if (ycs.currently_inverted) {
          return (
            <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mt: 2 }}>
              <strong>Yield curve is currently inverted</strong> — inversion alone is not the crash trigger;
              the danger peaks when the curve <em>un</em>inverts (returns to positive). Monitor closely.
            </Alert>
          );
        }
        return null;
      })()}

      <Divider sx={{ my: 4 }} />

      {/* ── Valuation KPIs ── */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
        Macro Valuation KPIs
      </Typography>

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : valuation ? (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              title="Shiller CAPE"
              value={valuation.shiller_cape?.value}
              label={valuation.shiller_cape?.label}
              severity={valuation.shiller_cape?.severity}
              description={valuation.shiller_cape?.description}
              mean={`${valuation.shiller_cape?.historical_mean} (long-run avg)`}
            >
              <Typography
                variant="caption"
                display="block"
                sx={{ mt: 0.5, color: valuation.shiller_cape?.stale ? 'error.main' : 'text.secondary' }}
              >
                Data: {valuation.shiller_cape?.data_date ?? '—'}
              </Typography>
              {valuation.shiller_cape?.stale && (
                <Tooltip
                  arrow
                  title={
                    `This CAPE reading is ${valuation.shiller_cape.stale_months} months old. ` +
                    `The live source has stopped responding and the frozen Yale archive is being ` +
                    `served instead, so this value — and the Collapse Radar and Stage Engine that ` +
                    `consume it — should not be trusted until the source is fixed.`
                  }
                >
                  <Chip
                    size="small"
                    icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
                    label={`Stale — ${valuation.shiller_cape.stale_months}mo old`}
                    sx={{ mt: 0.5, bgcolor: '#EF444422', color: '#EF4444', fontWeight: 700 }}
                  />
                </Tooltip>
              )}
            </KpiCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              title="US Buffett Indicator"
              value={valuation.us_buffett?.value}
              unit="%"
              label={valuation.us_buffett?.label}
              severity={valuation.us_buffett?.severity}
              description={valuation.us_buffett?.description}
              mean={`${valuation.us_buffett?.historical_mean_pct}%`}
            >
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Market cap: ${valuation.us_buffett?.market_cap_trillions}T ·
                GDP: ${valuation.us_buffett?.gdp_trillions}T
              </Typography>
            </KpiCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              title="Global Buffett Indicator"
              value={valuation.global_buffett?.value}
              unit="%"
              label={valuation.global_buffett?.label}
              severity={valuation.global_buffett?.severity}
              description={valuation.global_buffett?.description}
              mean={`${valuation.global_buffett?.historical_mean_pct}%`}
            >
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                World GDP: ${valuation.global_buffett?.world_gdp_trillions}T
                ({valuation.global_buffett?.world_gdp_year})
              </Typography>
            </KpiCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              title="VIX (Fear Index)"
              value={valuation.vix?.value}
              label={valuation.vix?.label}
              severity={valuation.vix?.severity}
              description={valuation.vix?.description}
              mean="19 (long-run avg)"
            >
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Below 15 = complacency · Above 30 = fear · Above 40 = panic
              </Typography>
            </KpiCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              title="S&P 500 Trailing P/E"
              value={valuation.sp500_trailing_pe?.value}
              label={valuation.sp500_trailing_pe?.label}
              severity={valuation.sp500_trailing_pe?.severity}
              description={valuation.sp500_trailing_pe?.description}
              mean={`${valuation.sp500_trailing_pe?.historical_mean} (historical)`}
            >
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                S&amp;P 500: ${valuation.sp500_trailing_pe?.sp500_price?.toLocaleString()}
              </Typography>
            </KpiCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              title="Yield Curve (10Y−2Y)"
              value={valuation.yield_curve?.value}
              unit="%"
              label={valuation.yield_curve?.label}
              severity={valuation.yield_curve?.severity}
              description={valuation.yield_curve?.description}
              mean={`${valuation.yield_curve?.historical_mean}% (historical mean)`}
            >
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                &lt;0% = inverted (recession signal) · Avg before recessions: −0.5%
              </Typography>
            </KpiCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <KpiCard
              title="Credit Spread (BAA10Y)"
              value={valuation.credit_spread?.value}
              unit="%"
              label={valuation.credit_spread?.label}
              severity={valuation.credit_spread?.severity}
              description={valuation.credit_spread?.description}
              mean={`${valuation.credit_spread?.historical_mean}% (historical mean)`}
            >
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                Moody BAA corp − 10Y Treasury · Tight = complacency · Wide = stress
              </Typography>
            </KpiCard>
          </Grid>

          {valuation.equity_risk_premium?.value != null && (
            <Grid item xs={12} sm={6} md={3}>
              <KpiCard
                title="Equity Risk Premium"
                value={valuation.equity_risk_premium.value}
                unit="%"
                label={valuation.equity_risk_premium.label}
                severity={valuation.equity_risk_premium.severity}
                description={valuation.equity_risk_premium.description}
                mean={`${valuation.equity_risk_premium.historical_mean}% (long-run avg)`}
              >
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Earnings yield: {valuation.equity_risk_premium.earnings_yield?.toFixed(2)}% −
                  10Y yield: {valuation.equity_risk_premium.ten_y_yield?.toFixed(2)}%
                </Typography>
              </KpiCard>
            </Grid>
          )}
        </Grid>
      ) : null}

      <Divider sx={{ my: 4 }} />

      {/* ── Market Internals: Breadth + LEI ── */}
      <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
        Market Internals
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Internal market health and leading economic indicators — often diverge from index levels before turning points.
      </Typography>
      {breadthLoading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
      ) : breadthData ? (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <KpiCard
              title="S&P 500 Breadth — % Above 200-Day MA"
              value={breadthData.breadth?.pct_above_200ma}
              unit="%"
              label={breadthData.breadth?.label}
              severity={breadthData.breadth?.severity}
              description={breadthData.breadth?.description}
            >
              {breadthData.breadth?.pct_above_200ma != null && (
                <Box sx={{ mt: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={breadthData.breadth.pct_above_200ma}
                    sx={{
                      height: 8, borderRadius: 4,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: riskColor(breadthData.breadth.severity ?? 'info'),
                      },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {breadthData.breadth.stocks_above} above · {breadthData.breadth.stocks_below} below (of {breadthData.breadth.total_checked} sampled)
                  </Typography>
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                &lt;40% = internal deterioration (danger) · &gt;80% = broad strength
              </Typography>
            </KpiCard>
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <KpiCard
              title="Conference Board LEI"
              value={breadthData.lei?.current_value}
              label={breadthData.lei?.label}
              severity={breadthData.lei?.severity}
              description={breadthData.lei?.description}
            >
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {breadthData.lei?.as_of && `As of: ${breadthData.lei.as_of} · `}
                MoM: {breadthData.lei?.mom_change != null
                  ? `${breadthData.lei.mom_change > 0 ? '+' : ''}${breadthData.lei.mom_change}`
                  : '—'}
                {' '}· 6-month: {breadthData.lei?.six_month_change != null
                  ? `${breadthData.lei.six_month_change > 0 ? '+' : ''}${breadthData.lei.six_month_change}`
                  : '—'}
              </Typography>
              {(breadthData.lei?.consecutive_months_declining ?? 0) >= 2 && (
                <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', mt: 0.5 }}>
                  Declining for {breadthData.lei.consecutive_months_declining} consecutive month(s)
                  {breadthData.lei.consecutive_months_declining >= 6 ? ' — recession warning threshold reached' : ''}
                </Typography>
              )}
            </KpiCard>
          </Grid>
        </Grid>
      ) : (
        <Alert severity="info">Market internals unavailable (fetching breadth data from yfinance may take 30–60s).</Alert>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: 'block' }}>
        Data sources: Robert Shiller / Yale (CAPE) · FRED (GDP, yield curve, HY spreads, LEI, DGS10) ·
        Wilshire Associates / yfinance (market cap, VIX, breadth) · World Bank (global GDP)
      </Typography>
    </Box>
  );
}
